import React from 'react';
import * as Sentry from '@sentry/browser';

import { useTranslation } from 'react-i18next';
import { Modal, Button, Message, ToastContext, ToggleLabel, TooltipHelp, Section } from '@weezevent/nacre';
import useAppContext from '../../../context/useAppContext';
import Style from './paidPostAction.module.css';
import { useNotifications, Notification } from '../../../components/notifications/notificationsProvider';

export default function PaidPostAction({ actionInstance, onClose }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const { addNotification } = useNotifications();

    const [{ event, organization }] = useAppContext();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [sendReceipt, setSendReceipt] = React.useState(false);

    const _filtered_orders = React.useMemo(() => {
        return actionInstance.filtered_items.filter(o => (o.buyer_email || o.buyer_email !== '') && o.origin !== 'weez_invitation');
    }, [actionInstance]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        actionInstance
            .runPostAction({ orders: actionInstance.filtered_items, send_receipt: sendReceipt })
            .then(response => {
                if (actionInstance.async) {
                    addNotification(new Notification('async_actions', actionInstance?.constructor.NAME, 'orders', response?.response?.task_id));
                    toast.info(t('ticket.manager.orders.action.generic.toast.async', { count: _filtered_orders.length }));
                } else {
                    toast.success(t('ticket.manager.orders.action.' + actionInstance.constructor.NAME + '.toast.success', { count: _filtered_orders.length }));
                }
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t('ticket.manager.orders.action.' + actionInstance.constructor.NAME + '.toast.error', { count: _filtered_orders.length }));
            })
            .finally(() => {
                setIsSubmitting(false);
                onClose();
            });
    }, [actionInstance, setIsSubmitting, onClose, event, organization, sendReceipt]);

    const _onClose = React.useCallback(() => {
        onClose();
        setIsSubmitting(false);
    }, [onClose]);

    return (
        <Modal
            open={Boolean(actionInstance)}
            onClose={_onClose}
            title={t('ticket.manager.orders.action.' + actionInstance.constructor.NAME + '.postAction.title', { count: _filtered_orders.length })}
            submitButton={<Button primary onClick={handleSubmit} disabled={isSubmitting} isLoading={isSubmitting} label={t('ticket.common.action.confirm')} />}
            cancelButton={<Button onClick={_onClose} label={t(`ticket.common.action.cancel`)} />}
        >
            <div>
                {actionInstance.filtered_items.length === 1
                    ? t('ticket.manager.orders.action.' + actionInstance.constructor.NAME + '.postAction.simpleBody')
                    : t('ticket.manager.orders.action.' + actionInstance.constructor.NAME + '.postAction.globalBody', {
                          count: _filtered_orders.length,
                          total: actionInstance.filtered_items.length
                      })}
                {_filtered_orders.length > 0 && (
                    <>
                        <div className={Style['message-container']}>
                            <Message type="default" title={t('attendees.addAttendees.modal.message.title')}>
                                {t('ticket.manager.orders.action.' + actionInstance.constructor.NAME + '.postAction.information', { count: actionInstance.filtered_items.length })}
                            </Message>
                        </div>
                        <Section>
                            <ToggleLabel
                                inlinePosition="left"
                                checked={sendReceipt}
                                label={
                                    <div className={Style['label-and-tooltip']}>
                                        {t('ticket.manager.orders.action.' + actionInstance.constructor.NAME + '.postAction.send-receipt')}
                                        <TooltipHelp
                                            mini
                                            triggerOnHover
                                            title={t('ticket.manager.orders.action.' + actionInstance.constructor.NAME + '.postAction.send-receipt-tooltip')}
                                        />
                                    </div>
                                }
                                onChange={(e, { checked }) => setSendReceipt(checked)}
                            />
                        </Section>
                    </>
                )}
            </div>
        </Modal>
    );
}
